import { Box, Divider, TextField, Typography, useMediaQuery } from '@material-ui/core';
import { makeStyles, Theme, createStyles, useTheme } from '@material-ui/core/styles';
import CartContentItem, { CartContentItemMeaskit } from './CartContentItem';
import { checkIfMeasKitSizeExists, calculateCompleteCartInvoice, getEndShipmentPrice, getNumberOfItems, getShipmentPriceSubsidyAmount, submitCouponRequest } from '../redux/CartRedux';
import { CartStateItem, CartState } from 'common-ts/dist/models/Cart';
import { useAppSelector, useAppDispatch } from '../reduxhooks';
import { ProductsList } from 'common-ts/dist/models/Product';
import { useFormik } from 'formik';
import * as yup from 'yup';
import OverlayComponentForm from './OverlayComponentForm';
import OverlayComponentButton from './OverlayComponentButton';
import OverlayComponentBaseContainerHorizontal from './OverlayComponentBaseContainerHorizontal';
import { Mixpanel } from '../mixpanel';
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';
import { OngkirBanner } from './OverlayViewPopupOngkir';
import React, { useEffect } from 'react';
import Countdown from 'react-countdown';
import { countdownRendererImgHalfwidth, countdownRendererNoText } from './PageProductDetail';
import { openOverlayMeaskitPrize } from '../redux/UIOverlayFlowVersionRedux';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cartContent: {
      display: 'block',
      position: 'relative',
      textAlign: 'center',
    },
    totalAmount: {
      padding: '5px 0px',
      display: 'flex',
      justifyContent: 'space-between',
    },
    dividerItemsTotal: {
      marginTop: '40px',
      marginBottom: '12px',
    },
    dividerCartTotal: {
      marginTop: '12px',
      marginBottom: '12px',
    },
    priceText: {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '15px',
      [theme.breakpoints.down('xs')]: {
        fontSize: '13px',
      }
    },
    priceStrikeText: {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '14px',
      textDecoration: 'line-through',
      marginRight: '10px',
      [theme.breakpoints.down('xs')]: {
        fontSize: '13px',
      }
    },
    shipmentText: {
      fontSize: '15px',
    },
    discountLabelText: {
      color: 'rgb(208, 21, 21)',
    },
    discountKeyText: {
      fontSize: '15px',
      [theme.breakpoints.down('xs')]: {
        fontSize: '13px',
      }
    },
    discountPriceText: {
      color: 'rgb(208, 21, 21)',
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '15px',
      [theme.breakpoints.down('xs')]: {
        fontSize: '13px',
      }
    },
    potonganOngkir: {
      padding: '5px 0px 0px 0px',
      display: 'flex',
      justifyContent: 'space-between',
      color: "#09B038"
    },
    potonganOngkirSubtextBox: {
      padding: '2px 0px 5px 0px',
      display: 'flex',
      justifyContent: 'space-between',
      color: "#09B038"
    },
    potonganOngkirTextIcon: {
      padding: "0 5px"
    },
    potonganOngkirText: {
      fontSize: "13px",
      fontWeight: 600,
      letterSpacing: "0.7px",
    },
    potonganOngkirSubtext: {
      fontSize: "10px",
      fontWeight: 400,
      letterSpacing: "0.4px",
    }
  }),
);

const validationSchema = yup.object({
  contentKey: yup.string()
  .min(5, '')
  .required('Masukkan kode promo kamu disini'),
});

export function CartCouponInput() {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const theme = useTheme();
  const isMdOrUp = useMediaQuery(theme.breakpoints.up("md"));

  const formik = useFormik({
    initialValues: {
      contentKey: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      Mixpanel.track('couponKeyInput', {
        'couponKey': values.contentKey,
      })
      dispatch(submitCouponRequest(values.contentKey));
    },
  });

  let children: JSX.Element[] = [];
  
  children.push(
    <TextField
      margin="dense"
      fullWidth
      id="contentKey"
      name="contentKey"
      label="Kode promo"
      type="text"
      value={formik.values.contentKey}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={false}
      helperText={(formik.touched.contentKey && formik.errors.contentKey)}
      InputLabelProps={{
        shrink: true,
      }}
    />
  );

  let submitChildren: JSX.Element[] = [];

  submitChildren.push(
    <OverlayComponentButton
      isTypeSubmit={true}
      formButtonText="Gunakan Kode"
      buttonMarginTopBottom="10px"
      buttonMarginLeftRight="0px"
    />
  )

  return (
    <OverlayComponentForm
      formTitle=""
      formSubmitBody={submitChildren}
      formFormBody={children}
      formHandleSubmit={formik.handleSubmit}
      titleWidthMdUp='400px'
      titleWidthSmDown='300px'
      formWidthMdUp='420px'
      formWidthSmDown='100%'
      formJustifyContent='space-evenly'
      formAlignItems='flex-start'
    />
  )
}

interface CartContentProps {
  cart: CartState,
  productsList: ProductsList,
  isModifiable: boolean
  withPromoField?: boolean
}

function CartContent({ cart, productsList, isModifiable, withPromoField }: CartContentProps) {
  const userBodyMeasurementsDict = useAppSelector(state => state.userMeasurementsList.userBodyMeasurementsDictionary);
  const userClothingMeasurementsDict = useAppSelector(state => state.userMeasurementsList.userClothingMeasurementsDictionary);
  const [lastRenderTime, setLastRenderTime] = React.useState<string>(new Date().toISOString());

  const userIsSignedInAndHasPurchased = useAppSelector(state => state.account.authState === 'signedin' && (state?.account.paidOrderCount ?? 0) >= 1);

  const cartProductList = cart.cartProductList;
  const cartShipmentPrice = cart.shipmentPrice;
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const giveMeasKit = userIsSignedInAndHasPurchased === false && cartProductList.length > 0;

  const thereIsMeasKitSize = checkIfMeasKitSizeExists(cart, userBodyMeasurementsDict);
  // if (thereIsMeasKitSize
  // && (cart.couponKey === "" || cart.coupon === undefined)
  // && cart.couponFetchStatus !== APIRequestStatus.RequestInProgress) {
  //   Mixpanel.track('couponKeyInput', {
  //     'couponKey': "CUSTOM-SIZE-EDIT-DISCOUNT",
  //   });
  //   dispatch(submitCouponRequest("CUSTOM-SIZE-EDIT-DISCOUNT"));
  // }

  // WE CALL THIS IN CART WINDOW
  // useEffect(() => {
  //   if (giveMeasKit) {
  //     dispatch(openOverlayMeaskitPrize());
  //   }
  // }, [giveMeasKit]);

  const completeCartInvoiceObj = calculateCompleteCartInvoice(cart, productsList, userBodyMeasurementsDict, userClothingMeasurementsDict);
  const originalPriceAmount = completeCartInvoiceObj.totalAmountObj.originalPriceAmount;
  const discountedPriceAmount = completeCartInvoiceObj.totalAmountObj.discountedPriceAmount;
  const discountedPriceAmountWithPromotions = completeCartInvoiceObj.totalAmountObj.discountedPriceAmountWithPromotions;
  const discountedPriceAmountWithPromotionsAndShipment = completeCartInvoiceObj.totalAmountObj.discountedPriceAmountWithPromotionsAndShipment;
  
  const cartItemsWithCouponPromotions = completeCartInvoiceObj.cartItemsWithCouponPromotions;

  const numberOfItemsInCart = getNumberOfItems(cart);
  const shipmentMaxPriceCut = getShipmentPriceSubsidyAmount(numberOfItemsInCart, discountedPriceAmountWithPromotions, cart?.couponKey ?? "");
  const endCartShipmentPrice = getEndShipmentPrice(discountedPriceAmountWithPromotions, getNumberOfItems(cart), cart.shipmentPrice, cart?.couponKey ?? "") as number;

  return (
    <div className={classes.cartContent}>
      <div>
        {
          cartProductList.map((item: CartStateItem) => 
          <CartContentItem cartItem={item} productsList={productsList} 
          cartItemsWithCouponPromotions={cartItemsWithCouponPromotions}
          isModifiable={isModifiable} parentRenderCallback={(renderTime) => {
            setLastRenderTime(renderTime);
          }}/>)
        }
        {
          giveMeasKit ? 
          <CartContentItemMeaskit isModifiable={isModifiable} />
          : null
        }
      </div>
      <Divider className={classes.dividerItemsTotal} />
      {
        withPromoField === false ? 
        null :
        <Box style={{marginLeft: '-24px', marginRight: '-24px',
          marginTop: '-13px' // STICKS TO TOP DIVIDER
        }}><img alt='label-nama-setelah-1'
        key={'label-nama-setelah-img'}
        style={{width: '100%'}} 
        src={'https://studiosclo.uk/general/web-general-label-nama-setelah-1.jpg'}/></Box>
      }
      {
        withPromoField === false ? 
        null :
        <CartCouponInput/>
      }
      {
        withPromoField === false ? 
        null :
        <OngkirBanner/>
      }
      {
        withPromoField === false ? 
        null :
        <Divider className={classes.dividerCartTotal} />
      }
      <div className={classes.totalAmount}>
        <Typography variant="body1">
          <b>
            ITEM(S) TOTAL:
          </b> 
        </Typography>
        <Typography className={classes.priceText}>
          <span className={classes.priceStrikeText} style={{color: withPromoField ? 'red' : 'black'}}>
            { 
            originalPriceAmount !== discountedPriceAmount ?
            "Rp " + originalPriceAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") :
            ""
            }
          </span>
          Rp { discountedPriceAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }
        </Typography>
      </div>
      {
        cart.couponKey && (discountedPriceAmount - discountedPriceAmountWithPromotions) > 0 ? 
        <>
        <div className={classes.totalAmount}>
          <Typography variant="body1">
            <b>
              PROMO CODE:
            </b> 
          </Typography>
          {
            cart.coupon?.couponHalfWidthCountdownMedia && (new Date().toISOString() < cart.coupon.expirationDateTime) ?
            <Box style={{margin: '-9px -2px -2px 0px', width: '48vw'}}>
              <Countdown
                date={cart.coupon.expirationDateTime}
                renderer={countdownRendererImgHalfwidth(cart.coupon.couponHalfWidthCountdownMedia)}
                onComplete={(timeDelta, completedOnStart) => {
                  setTimeout(() => {
                    setLastRenderTime(new Date().toISOString())
                  }, 400)
                }}
              />
            </Box>
            :
            <Typography className={classes.discountKeyText}>
              {cart.couponKey?.startsWith("AJAKTEMEN") ? "AJAKTEMEN" : cart.couponKey?.startsWith("WAPROMO") ? "WA PROMO" : cart.couponKey}
            </Typography>
          }
        </div> 
        <div className={classes.totalAmount}>
          <Typography variant="body1" className={classes.discountLabelText}>
            <b>
              PROMOTIONS:
            </b> 
          </Typography>
          {
            cart?.coupon && (new Date().toISOString() < cart.coupon.expirationDateTime) ?
            <Box style={{display: 'flex'}}>
            <Box style={{margin: '0px 5px 0px 0px'}}>
              <Countdown
                date={cart.coupon.expirationDateTime}
                renderer={countdownRendererNoText}
                onComplete={(timeDelta, completedOnStart) => {
                  setTimeout(() => {
                    setLastRenderTime(new Date().toISOString())
                  }, 400)
                }}
              />
            </Box>
            <Typography className={classes.discountPriceText}>
              <b>-</b> Rp {(discountedPriceAmount - discountedPriceAmountWithPromotions).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </Typography>
            </Box>
            :
            <Typography className={classes.discountPriceText}>
              <b>-</b> Rp {(discountedPriceAmount - discountedPriceAmountWithPromotions).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </Typography>
          }
        </div>
        <div className={classes.totalAmount}>
          <Typography variant="body1">
            <b>
              ITEM(S) SUBTOTAL:
            </b> 
          </Typography>
          <Typography className={classes.priceText}>
            <span className={classes.priceStrikeText}>
              { 
              discountedPriceAmountWithPromotions !== discountedPriceAmount ?
              "Rp " + discountedPriceAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") :
              ""
              }
            </span>
            Rp { discountedPriceAmountWithPromotions.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }
          </Typography>
        </div>
        </>: 
        null
      }
      <div className={classes.totalAmount}>
        <Typography variant="body1">
          <b>
            SHIPPING:
          </b> 
        </Typography>
        { 
        cartShipmentPrice === undefined ?
        <Typography className={classes.shipmentText}>
          -
        </Typography> :
        <Typography className={classes.priceText}>
          {"Rp " + cartShipmentPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
        </Typography>
        } 
      </div>
      {
        numberOfItemsInCart > 0 ?
        <div className={classes.potonganOngkir}>
          <Typography variant="body1" className={classes.potonganOngkirText}>
              POTONGAN ONGKIR<span className={classes.potonganOngkirTextIcon}>&#x24D8;</span>:
          </Typography>
          {
            cartShipmentPrice === undefined ? 
            <Typography className={classes.priceText}>
              {"Up to Rp " + shipmentMaxPriceCut.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </Typography>:
            <Typography className={classes.priceText}>
              {"- Rp " + (cartShipmentPrice - endCartShipmentPrice).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </Typography>
          }
        </div>
        : null
      }
      {
        numberOfItemsInCart > 0 ?
        <div className={classes.potonganOngkirSubtextBox}>
          <Typography variant="body1" className={classes.potonganOngkirSubtext}>
            Selamat! Kamu mendapat potongan ongkir!
          </Typography>
        </div>
        : null
      }
      <div className={classes.totalAmount}>
        <Typography variant="body1">
          <b>
            TOTAL:
          </b> 
        </Typography>
        <Typography className={classes.priceText}>
          <span className={classes.priceStrikeText}>
            { 
            discountedPriceAmount === discountedPriceAmountWithPromotions ?
            "" :
            cartShipmentPrice === undefined ?
            "Rp " + discountedPriceAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") :
            "Rp " + (discountedPriceAmount + cartShipmentPrice).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
          </span>
          Rp { 
          cartShipmentPrice === undefined ?
          discountedPriceAmountWithPromotions.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") :
          discountedPriceAmountWithPromotionsAndShipment.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
        </Typography>
      </div>
    </div>
  );
}

export default CartContent;